import isNullish from "utils/isNullish";

import { SITE_ACCESS_STATUS } from "../../../constants/sharepointAccess";
import errorHandler from "../../../handlers/errorHandler";
import {
  selectSharepoint,
  selectSharepointAuthRequestSeq,
  selectSharepointRequestSeq,
} from "../selectors";
import {
  applyPolicyReset,
  nextSharepointAuthRequest,
  nextSharepointRequest,
  setSharepointPolicyInfo,
} from "../sharepointSlice";
import { FIVE_MINUTES } from "./constants";

export const errorCodeOf = (error) =>
  error?.response?.data?.errorCode || error?.response?.data?.error;

export const statusOf = (error) =>
  error?.response?.status || error?.status || error?.code;

export const redirectUnlessSharepointError = (error) => {
  if (!errorCodeOf(error) && statusOf(error) === 401) {
    errorHandler(error?.response);
  }
};

export const beginRequest = (dispatch, getState) => {
  dispatch(nextSharepointRequest());
  return selectSharepointRequestSeq(getState());
};

export const isCurrentRequest = (getState, requestId) =>
  selectSharepointRequestSeq(getState()) === requestId;

export const beginAuthRequest = (dispatch, getState) => {
  dispatch(nextSharepointAuthRequest());
  return selectSharepointAuthRequestSeq(getState());
};

export const isCurrentAuthRequest = (getState, requestId) =>
  selectSharepointAuthRequestSeq(getState()) === requestId;

export const policyChanged = (sharepoint, restricted, policyRevision) =>
  !isNullish(sharepoint.policyRevision) &&
  (sharepoint.policyRevision !== policyRevision ||
    Boolean(sharepoint.restricted) !== Boolean(restricted));

export const isResolvedAccess = (accessStatus) =>
  Boolean(accessStatus) && accessStatus !== SITE_ACCESS_STATUS.CHECKING;

export const withPendingAccess = (rows, restricted, accessResults = {}) =>
  restricted
    ? rows.map((row) => {
        if (row.type !== "site" || isResolvedAccess(row.accessStatus)) {
          return row;
        }
        const known = accessResults[row.id];
        return known
          ? { ...row, ...known }
          : {
              ...row,
              accessible: null,
              accessStatus: SITE_ACCESS_STATUS.CHECKING,
            };
      })
    : rows;

export const applyPolicyInfo = (
  dispatch,
  getState,
  restricted,
  policyRevision
) => {
  const changed = policyChanged(
    selectSharepoint(getState()),
    restricted,
    policyRevision
  );
  if (changed) {
    dispatch(applyPolicyReset());
  }
  dispatch(setSharepointPolicyInfo({ restricted, policyRevision }));
  return changed;
};

export const pendingAccessIds = (sharepoint) =>
  (sharepoint.data || [])
    .filter(
      (item) =>
        item.type === "site" &&
        !(sharepoint.accessResults || {})[item.id] &&
        (!item.accessStatus ||
          item.accessStatus === SITE_ACCESS_STATUS.CHECKING)
    )
    .map((item) => item.id);

export const samePolicy = (sharepoint, revision) =>
  Boolean(sharepoint.restricted) && sharepoint.policyRevision === revision;

export const unknownOutcomes = (ids) =>
  ids.map((id) => ({
    id,
    accessible: null,
    accessStatus: SITE_ACCESS_STATUS.UNKNOWN,
  }));

export const outcomesFor = (batch, results = []) => {
  const answered = new Set(results.map((result) => result.id));
  return [
    ...results.filter((result) => batch.includes(result.id)),
    ...unknownOutcomes(batch.filter((id) => !answered.has(id))),
  ];
};

export const authorizationPolicyOf = (data) =>
  typeof data?.restricted === "boolean"
    ? {
        restricted: data.restricted,
        allowedSiteCount: Number(data.allowedSiteCount ?? 0),
      }
    : null;

export const readPendingRecord = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const data = JSON.parse(raw);
    localStorage.removeItem(key);

    if (Date.now() - data.timestamp > FIVE_MINUTES) return null;

    return data;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

export const isValidAuthUrl = (url) =>
  typeof url === "string" && url.startsWith("https://");

export const hasOfficeDialogApi = () =>
  Boolean(window.Office?.context?.ui?.displayDialogAsync);
